import Skeleton from '@mui/material/Skeleton';
import Icon from '__core/modules/atoms/Icons/Icon';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';

const DEFAULT_ON_CLICK_ICON = () => null;

const InputText = React.forwardRef(
  (
    {
      background = 'default',
      disabled = false,
      error,
      errorBorder,
      icon,
      iconColor = 'disabled',
      iconLeft = false,
      iconSize = 'small',
      isSkeleton = false,
      label,
      min,
      name,
      onChange = () => null,
      onClickIcon = DEFAULT_ON_CLICK_ICON,
      onKeyPress = () => {},
      placeholder,
      referer,
      suffix,
      type = 'text',
      variant = 'default',
      ...props
    },
    ref
  ) => {
    const refInput = referer ? referer : ref;

    function handleKeyDown(event) {
      if (min <= 0 && type === 'number' && event.key === '-') {
        event.preventDefault();
      }

      if ((event.key === 'Backspace' || event.key === 'Delete') && props.value === undefined) {
        onChange({ ...event, target: { ...event.target, value: '' } });
      }
    }

    //Focus con el teclado
    function handleFocus(event) {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          document.body.classList.add('keyboard-nav');
        }
      });
      
      document.addEventListener('mousedown', () => {
        document.body.classList.remove('keyboard-nav');
      });
      
      document.addEventListener('focusin', (e) => {
        if (document.body.classList.contains('keyboard-nav')) {
          e.target.classList.add('focused-by-keyboard');
        }
      });
      
      document.addEventListener('focusout', (e) => {
        e.target.classList.remove('focused-by-keyboard');
      });
    }

    return !isSkeleton ? (
      <div
        className={clsx(
          'input-text',
          `input-text--${variant}`,
          `input-text--background-${background}`,
          { 'input-text--icon': !!icon || !!suffix },
          { 'input-text--icon-click': onClickIcon !== DEFAULT_ON_CLICK_ICON },
          { 'input-text--icon-left': iconLeft },
          { 'input-text--error': !!error },
          { 'input-text--error-border': errorBorder }
        )}
      >
        {label && (
          <label className="input-text__label" title={label}>
            {label} {props.required && <sup>*</sup>}
          </label>
        )}

        <div className="input-text__input-container">
          <input
            className={clsx('input-text__input', { 'input-text__input--disabled': disabled })}
            data-testid={name}
            disabled={disabled}
            min={min}
            name={name}
            onChange={onChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            onKeyPress={onKeyPress}
            placeholder={placeholder}
            ref={refInput}
            type={type}
            {...props}
          />

          {suffix && (
            <div className="input-text__icon" onClick={onClickIcon}>
              <label className="input-text__suffix">{suffix}</label>
            </div>
          )}

          {icon && (
            <div className="input-text__icon" onClick={onClickIcon}>
              <Icon type={icon} size={iconSize} color={iconColor} />{' '}
            </div>
          )}
        </div>
        {error && <span className="input-text__error">{error}</span>}
      </div>
    ) : (
      <div>
        {label && (
          <label className="input-text__label">
            {label} {props.required && <sup>*</sup>}
          </label>
        )}

        <Skeleton animation="wave" height={48} variant="text" />
      </div>
    );
  }
);

InputText.propTypes = {
  background: PropTypes.oneOf(['default', 'grey']),
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  iconLeft: PropTypes.bool,
  isSkeleton: PropTypes.bool,
  min: PropTypes.number,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onClickIcon: PropTypes.func,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.any,
  variant: PropTypes.oneOf(['default']),
};

export default InputText;
